import { useMemo, type ReactNode } from "react";
import { SectionCard } from "../../../../components/common/section-card";
import { Text } from "../../../../components/common/typography";
import type { ChangeRequestDto } from "../../../../api";
import {
  ChangeRequestDtoStatusEnum,
  ChangeRequestDtoTypeEnum,
} from "../../../../api";
import { ChangesRequestedBanner } from "./changes-requested-banner";
import { ProposedChangesDiff, type ProductContext, type VariantLite } from "./proposed-changes-diff";
import { formatDate } from "../../../../utils/date";
import { useProductVariants } from "../../../products/hooks/useProduct";
import { useProductAttributeLines } from "../../../products/hooks/useProductAttributeLines";
import { useCountries } from "../../../products/hooks/useCountries";
import { useCategories } from "../../../products/hooks/useCategories";

export interface ChangeRequestDetailViewProps {
  request: ChangeRequestDto;
  banner?: ReactNode;
}

export function ChangeRequestDetailView({
  request,
  banner,
}: ChangeRequestDetailViewProps) {
  const showChangesRequested =
    request.status === ChangeRequestDtoStatusEnum.ChangesRequested;

  // Pull rich product context when this is a PRODUCT_UPDATE request so the
  // diff can render variant SKUs, country names, gallery summaries, etc.
  // instead of a raw JSON dump. SWR caches both calls.
  const { data: productData } = useProductVariants(request.targetProductId ?? 0);
  const { data: attributeLines } = useProductAttributeLines(request.targetProductId);
  const { data: countries } = useCountries();
  const { data: categories } = useCategories();

  // For PRODUCT_UPDATE the BE doesn't write a currentSnapshot (it does for
  // profile updates only — change-request.service.ts intentionally leaves it
  // null for products since the product's "current" state can be read live).
  // We synthesize one from the live product so the diff renders before/after
  // for every scalar field the seller changed, not just variant prices.
  // Keys not present in proposedChanges are omitted so we don't show unrelated
  // rows.
  const proposedChanges = (request.proposedChanges ?? {}) as Record<string, unknown>;
  const effectiveCurrentValues = useMemo<Record<string, unknown> | undefined>(() => {
    if (request.currentSnapshot) {
      return request.currentSnapshot as Record<string, unknown>;
    }
    if (request.type !== ChangeRequestDtoTypeEnum.ProductUpdate) return undefined;
    if (!productData?.product) return undefined;
    const p = productData.product;
    const synth: Record<string, unknown> = {};
    const has = (k: string) => Object.prototype.hasOwnProperty.call(proposedChanges, k);
    if (has("name")) synth.name = p.displayName ?? p.name ?? null;
    if (has("description")) synth.description = p.description ?? null;
    if (has("countryOfOriginId")) synth.countryOfOriginId = p.countryOfOrigin?.id ?? null;
    if (has("categoryId")) synth.categoryId = p.category?.id ?? null;
    if (has("compatibleWarrantyIds")) {
      synth.compatibleWarrantyIds = p.compatibleWarrantyIds ?? [];
    }
    if (has("active")) synth.active = p.active ?? null;
    return Object.keys(synth).length > 0 ? synth : undefined;
  }, [request.currentSnapshot, request.type, productData, proposedChanges]);

  const productContext = useMemo<ProductContext | undefined>(() => {
    if (request.targetProductId == null) return undefined;
    const variantsById = new Map<number, VariantLite>();
    for (const v of productData?.variants ?? []) {
      variantsById.set(v.id, {
        id: v.id,
        sku: v.defaultCode ?? null,
        displayName: v.displayName ?? v.name ?? `Variant #${v.id}`,
        currentPrice: v.sellerPrice ?? null,
        currentStock: v.supplierStock ?? null,
        currentDiscount: v.discountPercentage ?? null,
      });
    }
    const countryNameById = new Map<number, string>();
    for (const c of countries ?? []) countryNameById.set(c.id, c.name);
    const categoryNameById = new Map<number, string>();
    for (const c of categories ?? []) {
      categoryNameById.set(c.id, c.completeName ?? c.displayName ?? c.name);
    }
    const attributeValueNameById = new Map<number, string>();
    for (const line of attributeLines ?? []) {
      for (const val of line.values ?? []) attributeValueNameById.set(val.id, val.name);
    }
    const galleryImageIds = productData?.product?.galleryImageIds ?? [];
    return {
      templateName: productData?.product?.displayName ?? productData?.product?.name,
      variantsById,
      countryNameById,
      categoryNameById,
      attributeValueNameById,
      galleryImageIds,
    };
  }, [request.targetProductId, productData, attributeLines, countries, categories]);

  return (
    <>
      {banner}
      {showChangesRequested && (
        <ChangesRequestedBanner
          reviewNote={request.reviewNote}
          reviewedAt={request.reviewedAt}
        />
      )}

      <SectionCard title="Proposed changes" shadow className="w-full">
        <ProposedChangesDiff
          proposedChanges={proposedChanges}
          currentValues={effectiveCurrentValues}
          productContext={productContext}
        />
      </SectionCard>

      <SectionCard title="Details" shadow className="w-full">
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {request.targetProductId != null && (
            <div>
              <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">
                Target product
              </Text>
              <Text variant="bodySm" tone="dark" className="mt-1">
                {productContext?.templateName
                  ? `${productContext.templateName} (#${request.targetProductId})`
                  : `#${request.targetProductId}`}
              </Text>
            </div>
          )}
          <div>
            <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">
              Submitted
            </Text>
            <Text variant="bodySm" tone="dark" className="mt-1">
              {request.submittedAt ? formatDate(request.submittedAt, true) : "—"}
            </Text>
          </div>
          <div>
            <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">
              Last updated
            </Text>
            <Text variant="bodySm" tone="dark" className="mt-1">
              {formatDate(request.updatedAt, true)}
            </Text>
          </div>
          {request.reviewedAt && (
            <div>
              <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">
                Reviewed
              </Text>
              <Text variant="bodySm" tone="dark" className="mt-1">
                {formatDate(request.reviewedAt, true)}
              </Text>
            </div>
          )}
          {request.note && (
            <div className="md:col-span-2">
              <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">
                Submitter note
              </Text>
              <Text
                variant="bodySm"
                tone="dark"
                className="mt-1 whitespace-pre-wrap"
              >
                {request.note}
              </Text>
            </div>
          )}
          {request.rejectionReason && (
            <div className="md:col-span-2">
              <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">
                Rejection reason
              </Text>
              <Text
                variant="bodySm"
                tone="dark"
                className="mt-1 whitespace-pre-wrap"
              >
                {request.rejectionReason}
              </Text>
            </div>
          )}
        </dl>
      </SectionCard>
    </>
  );
}
